import { useState } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  error: string | null;
  success: boolean;
}

interface MaterialData {
  title: string;
  description: string;
  keywords: string[];
  introductionUrl: string; // URL do vídeo de introdução (YouTube, Vimeo, etc)
  purchaseUrl: string;
  referenceIds: string[];
  isFree: boolean;
  templateId?: string;
  contentIds?: string[];
  preview?: boolean; // Flag indicando se o material terá preview customizado
}

export const useThumbnailUpload = () => {
  const [uploadState, setUploadState] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  // Função para criar material e fazer upload da thumbnail e preview (opcional) em uma única operação
  const createMaterialWithThumbnail = async (
    materialData: MaterialData,
    thumbnailFile: File,
    previewFile?: File | null
  ): Promise<{ material: any; thumbnailUrl: string; previewImageUrl?: string } | null> => {
    setUploadState({
      isUploading: true,
      progress: 0,
      error: null,
      success: false,
    });

    try {
      setUploadState(prev => ({ ...prev, progress: 15 }));

      const materialResponse = await fetchWithAuth(API_ROUTES.MATERIALS.create(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(materialData),
      });

      if (!materialResponse.ok) {
        const errorText = await materialResponse.text();
        throw new Error(`Erro ao criar material: ${materialResponse.status} - ${errorText}`);
      }

      const responseData = await materialResponse.json();
      
      const { id, thumbnailUrl, previewUrl } = responseData;

      setUploadState(prev => ({ ...prev, progress: 30 }));

      // 2. Fazer upload da thumbnail para a URL presigned
      const uploadResponse = await fetch(thumbnailUrl, {
        method: 'PUT',
        body: thumbnailFile,
        headers: {
          'Content-Type': thumbnailFile.type,
        },
      });

      if (!uploadResponse.ok) {
        const errorBody = await uploadResponse.text();
        throw new Error(`Erro no upload da thumbnail para S3: ${uploadResponse.status} - ${errorBody}`);
      }
      
      // 3. Construir URL final da thumbnail (removendo query parameters)
      const baseThumbnailUrl = thumbnailUrl.split('?')[0];
      const finalThumbnailUrl = baseThumbnailUrl;

      setUploadState(prev => ({ ...prev, progress: 60 }));

      // 4. Se houver arquivo de preview, fazer upload
      let finalPreviewImageUrl: string | undefined;
      if (previewFile && previewUrl) {
        const previewUploadResponse = await fetch(previewUrl, {
          method: 'PUT',
          body: previewFile,
          headers: {
            'Content-Type': previewFile.type,
          },
        });

        if (!previewUploadResponse.ok) {
          const errorBody = await previewUploadResponse.text();
          throw new Error(`Erro no upload do preview para S3: ${previewUploadResponse.status} - ${errorBody}`);
        }

        const basePreviewUrl = previewUrl.split('?')[0];
        finalPreviewImageUrl = basePreviewUrl;
        
        setUploadState(prev => ({ ...prev, progress: 90 }));
      }

      setUploadState(prev => ({ ...prev, progress: 100, success: true }));

      return {
        material: { id, createdAt: responseData.createdAt },
        thumbnailUrl: finalThumbnailUrl,
        previewImageUrl: finalPreviewImageUrl
      };

    } catch (error) {
      setUploadState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isUploading: false,
      }));
      return null;
    }
  };

  const resetUploadState = () => {
    setUploadState({
      isUploading: false,
      progress: 0,
      error: null,
      success: false,
    });
  };

  return {
    createMaterialWithThumbnail,
    uploadState,
    resetUploadState,
  };
};
